/* istanbul ignore file */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { FuseClassPrefixProvider } from '@tvg/fuse/class-prefix';
import React from 'react';
import { hydrateRoot } from 'react-dom/client';
import { FUSE_CLASS_PREFIX } from '../../constants/fuseClassPrefix';
import type { ClientHydration } from '../../utils/types';

declare global {
	interface Window {
		__TMPL_HYDRATE__: ClientHydration[];
	}
}

type ComponentMap = {
	[key: string]: () => Promise<any>;
};

const componentMap: ComponentMap = {
	Accordion: () =>
		import(/* webpackChunkName: "accordion" */ '../../components/seo-accordion/seo-accordion'),
	AccordionGroup: () =>
		import(
			/* webpackChunkName: "accordion-group" */ '../../components/accordion-group/accordion-group'
		),
	AccordionGroupJsonLd: () =>
		import(
			/* webpackChunkName: "accordion-group-json-ld" */ '../../components/accordion-group-json-ld/accordion-group-json-ld'
		),
	Alert: () => import(/* webpackChunkName: "alert" */ '../../components/alert/alert'),
	AnimatedImage: () =>
		import(
			/* webpackChunkName: "animation-image" */ '../../components/animated-image/animated-image'
		),
	BrandCardCarousel: () =>
		import(
			/* webpackChunkName: "brand-card-carousel" */ '../../components/brand-card-carousel/brand-card-carousel'
		),
	BrandCarousel: () =>
		import(
			/* webpackChunkName: "brand-carousel" */ '../../components/brand-carousel/brand-carousel'
		),
	CardGroup: () =>
		import(/* webpackChunkName: "card-group" */ '../../components/card-group/card-group'),
	ContentBanner1: () =>
		import(
			/* webpackChunkName: "content-banner1" */ '../../components/content-banner-1/content-banner-1'
		),
	ContentBanner2: () =>
		import(
			/* webpackChunkName: "content-banner2" */ '../../components/content-banner-2/content-banner-2'
		),
	ContentPattern: () =>
		import(
			/* webpackChunkName: "content-pattern" */ '../../components/content-pattern/content-pattern'
		),
	ContentTileGroup: () =>
		import(
			/* webpackChunkName: "content-tile-group" */ '../../components/content-tile-group/content-tile-group'
		),
	CopyCode: () =>
		import(/* webpackChunkName: "copy-code" */ '../../components/copy-code/copy-code'),
	ImageHero: () =>
		import(/* webpackChunkName: "image-hero" */ '../../components/image-hero/image-hero'),
	ImageHero2: () =>
		import(/* webpackChunkName: "image-hero2" */ '../../components/image-hero-2/image-hero-2'),
	ImageHero3: () =>
		import(/* webpackChunkName: "image-hero3" */ '../../components/image-hero-3/image-hero-3'),
	ImageHeroCarousel: () =>
		import(
			/* webpackChunkName: "image-hero-carousel" */ '../../components/image-hero-carousel/image-hero-carousel'
		),
	ImageHero3Carousel: () =>
		import(
			/* webpackChunkName: "image-hero3-carousel" */ '../../components/image-hero-3-carousel/image-hero-3-carousel'
		),
	InstaShop: () =>
		import(/* webpackChunkName: "insta-shop" */ '../../components/insta-shop/insta-shop'),

	MediaHero: () =>
		import(/* webpackChunkName: "media-hero" */ '../../components/media-hero/media-hero'),

	MediaHeroQuickLinks: () =>
		import(
			/* webpackChunkName: "media-hero-quick-links" */ '../../components/media-hero-quick-links/media-hero-quick-links'
		),

	Modal: () => import(/* webpackChunkName: "modal" */ '../../components/modal/modal'),
	LoanCalculator: () =>
		import(
			/* webpackChunkName: "loan-calculator" */ '../../components/loan-calculator/loan-calculator'
		),
	LoopedVideo: () =>
		import(/* webpackChunkName: "looped-video" */ '../../components/looped-video/looped-video'),
	PersonalisedContentBanner: () =>
		import(
			/* webpackChunkName: "personalised-content-banner" */ '../../components/personalised-content-banner/personalised-content-banner'
		),
	QuickFilterCarousel: () =>
		import(
			/* webpackChunkName: "quick-filter-carousel" */ '../../components/quick-filter-carousel/quick-filter-carousel'
		),
	RepaymentCalculator: () =>
		import(
			/* webpackChunkName: "repayment-calculator" */ '../../components/repayment-calculator/repayment-calculator'
		),
	SecondaryBanner: () =>
		import(
			/* webpackChunkName: "secondary-banner" */ '../../components/secondary-banner/secondary-banner'
		),
	SimpleBanner: () =>
		import(/* webpackChunkName: "simple-banner" */ '../../components/simple-banner/simple-banner'),
	MultiMessageBanner: () =>
		import(
			/* webpackChunkName: "multi-message-banner" */ '../../components/multi-message-banner/multi-message-banner'
		),
	Table: () => import(/* webpackChunkName: "table" */ '../../components/table/table'),
	Tabs: () => import(/* webpackChunkName: "tabs" */ '../../components/tabs/tabs'),
	TextBlock: () =>
		import(/* webpackChunkName: "text-block" */ '../../components/text-block/text-block'),
	VideoHero: () =>
		import(/* webpackChunkName: "video-hero" */ '../../components/video-hero/video-hero'),
	VideoPlayer: () =>
		import(/* webpackChunkName: "video-player" */ '../../components/video-player/video-player'),
	WidescreenCarousel: () =>
		import(
			/* webpackChunkName: "widescreen-carousel" */ '../../components/widescreen-carousel/widescreen-carousel'
		),
};

if (window.__TMPL_HYDRATE__) {
	window.__TMPL_HYDRATE__.forEach(({ id, reference, props }) => {
		if (reference in componentMap) {
			componentMap[reference]().then((lib) => {
				const Component = lib[reference] as any;
				const container = document.getElementById(id);

				try {
					if (!container) {
						throw new Error(`Container with id "${id}" not found.`);
					}

					hydrateRoot(
						container,
						<FuseClassPrefixProvider classPrefix={FUSE_CLASS_PREFIX}>
							<Component {...props} />
						</FuseClassPrefixProvider>,
					);
				} catch (error) {
					throw new Error(`Error hydrating component: ${error}`);
				}
			});
		}
	});
}
